import React, { FunctionComponent, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { AxiosError } from 'axios'
import styled from 'styled-components/native'

import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { InlineSuccessBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineSuccessBanner/InlineSuccessBanner'
import { ConfirmationModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/confirmationModal/ConfirmationModal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { ADMIN } from '../../common/constants/routingConstants'
import { AdminCard, AdminPageLayout } from '../components/AdminPageLayout'
import { CatalogImportReview } from './components/CatalogImportReview'
import { NpCatalogImportResponse, useImportNpCatalogMutation } from './data/npCatalogImportApi'

const GOOGLE_SHEET_URL_REGEX = /^https:\/\/docs\.google\.com\/spreadsheets\/d\/[\w-]+/
const CONFLICT_STATUS = 409

const isImportSummary = (data: unknown): data is NpCatalogImportResponse =>
  typeof data === 'object' && data !== null && 'counts' in data && 'errors' in data

const getErrorMessage = (error: unknown) => {
  const data = (error as AxiosError<{ message?: string }>)?.response?.data
  return (
    (typeof data === 'object' && data?.message) || (error as Error)?.message || 'The request failed. Please try again.'
  )
}

const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

const getConfirmationText = ({ counts }: NpCatalogImportResponse) =>
  `This will write to the NP test catalog: ${pluralize(counts.measures_created, 'measure')} created, ` +
  `${pluralize(counts.measures_updated, 'measure')} updated, ${pluralize(counts.batteries_created, 'battery')} created, ` +
  `${pluralize(counts.batteries_reactivated, 'battery')} reactivated and ` +
  `${pluralize(counts.batteries_deprecated, 'battery')} deprecated. Batteries the sheet no longer produces are deprecated.`

const ButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['16px'],
}))

const NpProgramAdmin: FunctionComponent = () => {
  const navigate = useNavigate()
  const [sheetUrl, setSheetUrl] = useState('')
  // Dry-run summary, or the summary of a confirmation the backend rejected (can_apply is false).
  const [review, setReview] = useState<NpCatalogImportResponse>()
  const [imported, setImported] = useState<NpCatalogImportResponse>()
  const [errorMessage, setErrorMessage] = useState<string>()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [importNpCatalog, { isLoading }] = useImportNpCatalogMutation()
  // Latest URL typed, so a response that arrives after the URL changed can be discarded.
  const currentUrl = useRef('')

  const trimmedUrl = sheetUrl.trim()
  const isValidUrl = GOOGLE_SHEET_URL_REGEX.test(trimmedUrl)
  const showUrlError = trimmedUrl.length > 0 && !isValidUrl
  // The fingerprint belongs to the sheet that was previewed, so it is dropped whenever the URL changes.
  const canImport = isValidUrl && !!review?.can_apply

  const resetResults = () => {
    setReview(undefined)
    setImported(undefined)
    setErrorMessage(undefined)
  }

  const onChangeUrl = (value: string) => {
    setSheetUrl(value)
    currentUrl.current = value.trim()
    resetResults()
  }

  const onPreview = async () => {
    resetResults()
    const requestedUrl = trimmedUrl
    try {
      const summary = await importNpCatalog({ sheetUrl: requestedUrl, dryRun: true }).unwrap()
      // The URL was edited while the preview was loading: its fingerprint no longer matches the field.
      if (currentUrl.current === requestedUrl) setReview(summary)
    } catch (error) {
      if (currentUrl.current === requestedUrl) setErrorMessage(getErrorMessage(error))
    }
  }

  const onImport = async () => {
    setIsConfirmOpen(false)
    setErrorMessage(undefined)
    try {
      setImported(
        await importNpCatalog({ sheetUrl: trimmedUrl, dryRun: false, fingerprint: review?.fingerprint }).unwrap(),
      )
      setReview(undefined)
    } catch (error) {
      const axiosError = error as AxiosError
      const data = axiosError?.response?.data
      if (isImportSummary(data)) {
        // Rejected with the sheet's validation errors: show them with the same review view as the preview.
        setReview(data)
      } else if (axiosError?.response?.status === CONFLICT_STATUS) {
        // The fingerprint is stale; a new preview is required before importing again.
        setReview(undefined)
      }
      setErrorMessage(isImportSummary(data) ? undefined : getErrorMessage(error))
    }
  }

  return (
    <AdminPageLayout
      title='NP Program Admin'
      subtitle='Tools for managing the NP program.'
      testID={tID('NpProgramAdmin')}
      onBack={() => navigate(ADMIN.route)}
    >
      <AdminCard
        title='Import NP Test Catalog'
        description='Import NP testing measures and standard batteries from a Google Sheet. Run a preview first, then confirm the import.'
      >
        <InputField
          label='Google Sheet URL'
          name='NpProgramAdmin-sheetUrl'
          value={sheetUrl}
          onChange={(e) => onChangeUrl(e.nativeEvent.text)}
          error={showUrlError ? 'Enter a valid Google Sheets URL' : undefined}
          autoCapitalize='none'
        />
        <ButtonRow>
          <SecondaryButton
            text='Preview Import'
            testID={tID('NpProgramAdmin-preview')}
            onPress={onPreview}
            disabled={!isValidUrl || isLoading}
            loading={isLoading && !review}
          />
          <PrimaryButton
            text='Import Catalog'
            testID={tID('NpProgramAdmin-import')}
            onPress={() => setIsConfirmOpen(true)}
            disabled={!canImport || isLoading}
            loading={isLoading && !!review}
          />
        </ButtonRow>
        {errorMessage && <InlineErrorBanner testId={tID('NpProgramAdmin-error')} text={errorMessage} />}
        {imported && (
          <InlineSuccessBanner
            testId={tID('NpProgramAdmin-success')}
            text={`Catalog imported: ${pluralize(imported.counts.measures_created, 'measure')} created, ${pluralize(
              imported.counts.measures_updated,
              'measure',
            )} updated, ${pluralize(imported.counts.batteries_created, 'battery')} created.`}
          />
        )}
      </AdminCard>
      {review && (
        <AdminCard title='Preview'>
          <CatalogImportReview summary={review} />
        </AdminCard>
      )}
      {review && (
        <ConfirmationModal
          testID={tID('NpProgramAdmin-confirm')}
          visible={isConfirmOpen}
          onRequestClose={() => setIsConfirmOpen(false)}
          modalTitle='Import NP Test Catalog?'
          modalContents={getConfirmationText(review)}
          confirmationButtonText='Import Catalog'
          cancelButtonText='Cancel'
          onConfirmationButtonPress={onImport}
        />
      )}
    </AdminPageLayout>
  )
}

export default NpProgramAdmin
